import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import JobCard from "@/components/JobCard";
import { Search, MapPin, Filter, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const JOB_TYPES = ["part-time", "full-time", "internship", "seasonal", "volunteer", "apprenticeship", "gig"];
const INDUSTRIES = ["retail", "food service", "tech", "nonprofit", "healthcare", "sports", "entertainment", "government", "trades", "education", "customer service"];
const ORG_TYPES = [
  ["", "Any"], ["business", "Business"], ["church", "Church"], ["nonprofit", "Nonprofit"],
  ["city", "City"], ["county", "County"], ["school-district", "School District"], ["government", "Government"],
];
const SOURCES = ["", "employer", "sample", "remotive", "adzuna", "usajobs", "jooble"];

export default function Jobs() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);

  const p = (k, def = "") => params.get(k) ?? def;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const query = {};
      ["q","location","age","job_type","industry","organization_type","source"].forEach(k=>{
        const v = params.get(k); if (v) query[k] = v;
      });
      ["remote","school_friendly","only_eligible","summer_only","near_me"].forEach(k=>{
        if (params.get(k) === "true") query[k] = true;
      });
      if (params.get("experience_required") === "false") query.experience_required = false;
      if (query.age) query.age = Number(query.age);
      const { data } = await api.get("/jobs", { params: query });
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
      setBracket(data.user_bracket);
      setLoading(false);
    };
    load();
  }, [params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const showAgeChip = user?.role === "youth" && user?.age && !p("age");

  const FilterCheck = ({ k, label, testid }) => (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={p(k)==="true"} onChange={(e)=>setParam(k, e.target.checked ? "true" : "")} data-testid={testid}/>
      <span className="text-sm font-semibold">{label}</span>
    </label>
  );

  return (
    <div className="nh-container py-10 grid md:grid-cols-12 gap-8">
      <aside className="md:col-span-3">
        <div className="nh-card p-5 sticky top-24">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18}/>
            <h2 className="font-display font-black text-xl">Filters</h2>
          </div>

          <label className="text-xs font-bold uppercase tracking-wide">Keyword</label>
          <div className="flex items-center border-2 border-black rounded-lg mb-3 mt-1">
            <Search size={16} className="ml-2"/>
            <input className="w-full px-2 py-2 outline-none" defaultValue={p("q")}
              onKeyDown={(e)=>{ if (e.key==="Enter") setParam("q", e.currentTarget.value); }}
              onBlur={(e)=>setParam("q", e.target.value)}
              data-testid="filter-keyword"/>
          </div>

          <label className="text-xs font-bold uppercase tracking-wide">Location</label>
          <div className="flex items-center border-2 border-black rounded-lg mb-3 mt-1">
            <MapPin size={16} className="ml-2"/>
            <input className="w-full px-2 py-2 outline-none" defaultValue={p("location")}
              onBlur={(e)=>setParam("location", e.target.value)}
              data-testid="filter-location"/>
          </div>

          <label className="text-xs font-bold uppercase tracking-wide">Your Age</label>
          <select value={p("age")} onChange={(e)=>setParam("age", e.target.value)} className="nh-input mb-3 mt-1" data-testid="filter-age">
            <option value="">Any age</option>
            {[13,14,15,16,17,18,19,20,21].map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          <label className="text-xs font-bold uppercase tracking-wide">Job Type</label>
          <select value={p("job_type")} onChange={(e)=>setParam("job_type", e.target.value)} className="nh-input mb-3 mt-1" data-testid="filter-jobtype">
            <option value="">Any type</option>
            {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label className="text-xs font-bold uppercase tracking-wide">Industry</label>
          <select value={p("industry")} onChange={(e)=>setParam("industry", e.target.value)} className="nh-input mb-3 mt-1" data-testid="filter-industry">
            <option value="">Any industry</option>
            {INDUSTRIES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label className="text-xs font-bold uppercase tracking-wide">Organization</label>
          <select value={p("organization_type")} onChange={(e)=>setParam("organization_type", e.target.value)} className="nh-input mb-3 mt-1" data-testid="filter-org-type">
            {ORG_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          <label className="text-xs font-bold uppercase tracking-wide">Source</label>
          <select value={p("source")} onChange={(e)=>setParam("source", e.target.value)} className="nh-input mb-3 mt-1" data-testid="filter-source">
            {SOURCES.map(s => <option key={s} value={s}>{s || "Any"}</option>)}
          </select>

          <div className="space-y-2 mt-3">
            <FilterCheck k="only_eligible" label="Only jobs I'm eligible for 🟢" testid="filter-only-eligible"/>
            <FilterCheck k="remote" label="Remote only" testid="filter-remote"/>
            <FilterCheck k="school_friendly" label="Student-friendly" testid="filter-school-friendly"/>
            <FilterCheck k="summer_only" label="Summer jobs" testid="filter-summer"/>
            <FilterCheck k="near_me" label="Near me (uses your state)" testid="filter-near-me"/>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={p("experience_required")==="false"} onChange={(e)=>setParam("experience_required", e.target.checked ? "false" : "")} data-testid="filter-no-exp"/>
              <span className="text-sm font-semibold">No experience needed</span>
            </label>
          </div>

          <button className="nh-btn nh-btn-outline w-full mt-4 text-xs" onClick={()=>setParams(new URLSearchParams())} data-testid="filter-reset">
            Reset filters
          </button>
        </div>
      </aside>

      <div className="md:col-span-9">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl">
              {loading ? "Loading..." : `${total} jobs`}{p("q") ? ` matching “${p("q")}”`: ""}
            </h1>
            {showAgeChip && (
              <p className="text-sm text-nh-muted mt-1 flex items-center flex-wrap gap-1">
                <CheckCircle2 size={14} className="text-emerald-600"/>
                Showing only jobs legally open at age <b>{user.age}</b>{bracket && <> · bracket <b>{bracket}</b></>}
                <button className="underline ml-1" onClick={()=>setParam("age", "")} data-testid="clear-age">show all</button>
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center"><div className="inline-block h-8 w-8 border-4 border-black border-t-transparent rounded-full animate-spin"/></div>
        ) : jobs.length === 0 ? (
          <div className="nh-card p-8 text-center">
            <p className="font-display text-xl font-black">No jobs match your filters yet.</p>
            <p className="text-sm text-nh-muted mt-2">Try broadening your search, or explore <a href="/career-coach" className="underline">AI Career Coach</a> for alternatives.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {jobs.map(j => <JobCard key={j.id} job={j}/>)}
          </div>
        )}
      </div>
    </div>
  );
}
